import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { fallbackCopyTextToClipboard } from 'js/lib/MiscUtils';

interface EmailCellProps {
  email: string;
}

const truncationStyles = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const emailCellStyles = {
  color: 'inherit',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: 'primary.main',
    textDecoration: 'underline',
    textDecorationThickness: '1.5px',
    textUnderlineOffset: '2px',
  },
  '&:focus': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '2px',
    borderRadius: '2px',
  },
  '&:active': {
    color: 'primary.dark',
  },
};

function useIsOverflowed(ref: React.RefObject<HTMLElement | null>, content: string) {
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const checkOverflow = () => {
      setIsOverflowed(el.scrollWidth > el.clientWidth + 1);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [content]);

  return isOverflowed;
}

export function EmailCell({ email }: EmailCellProps) {
  const [isCopied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const isOverflowed = useIsOverflowed(textRef, email);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleCopy = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      await fallbackCopyTextToClipboard(email);
      setCopied(true);
      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 1500);
    } catch (error) {
      console.error('An error occurred copying email:', error);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    handleCopy(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCopy(e);
    }
  };

  if (!email) return null;

  const tooltipTitle = isCopied ? 'Email copied' : isOverflowed ? email : 'Click to copy email';

  return (
    <Tooltip arrow leaveDelay={600} title={tooltipTitle} placement="top-start">
      <Box
        ref={textRef}
        component="span"
        sx={truncationStyles}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`Copy email ${email} to clipboard`}
      >
        <Typography component="span" sx={emailCellStyles}>
          {email}
        </Typography>
      </Box>
    </Tooltip>
  );
}
